import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import { Popup as AntdMobilePopup } from "antd-mobile";
import { useLocation } from "react-router-dom";
import { useUserStore } from "../../modules/user/stores";
import { openLoginRegisterPopup } from "../LoginRegisterPopup";
import {
  DEPOSIT_POPUP_EVENT,
  CLOSE_DEPOSIT_POPUP_EVENT,
} from "./triggerDepositPopup";
import type {
  DepositPopupProps,
  OpenDepositPopupOptions,
} from "./DepositPopup.types";
import ContentSkeleton from "../ContentSkeleton/ContentSkeleton";
import DepositInsuranceImg from "@/assets/homePage/reskin/deposit_insurance.webp";
import "./DepositPopup.scss";
import { useT } from "../../i18n/hooks";

const LazyRecharge = lazy(() => import("../../pages/Recharge/Recharge"));

export const DepositPopup: React.FC<DepositPopupProps> = ({
  className = "",
}) => {
  const location = useLocation();
  const t = useT();
  const isAuthenticated = useUserStore(state => state.isAuthenticated());
  const [visible, setVisible] = useState(false);
  const [key, setKey] = useState(0);
  const [openOptions, setOpenOptions] = useState<OpenDepositPopupOptions>({});
  const openedAtPathRef = useRef<string>("");

  const handleOpen = useCallback(
    (e: Event) => {
      if (!isAuthenticated) {
        openLoginRegisterPopup({ defaultTab: "password" });
        return;
      }
      const detail = (e as CustomEvent<OpenDepositPopupOptions>).detail;
      if (detail?.activityId) {
        const url = new URL(window.location.href);
        url.searchParams.set("activityId", detail.activityId);
        window.history.replaceState(null, "", url.toString());
      }
      setOpenOptions(detail || {});
      openedAtPathRef.current = window.location.pathname;
      setKey(prev => prev + 1);
      setVisible(true);
    },
    [isAuthenticated]
  );

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    window.addEventListener(DEPOSIT_POPUP_EVENT, handleOpen);
    window.addEventListener(CLOSE_DEPOSIT_POPUP_EVENT, handleClose);
    return () => {
      window.removeEventListener(DEPOSIT_POPUP_EVENT, handleOpen);
      window.removeEventListener(CLOSE_DEPOSIT_POPUP_EVENT, handleClose);
    };
  }, [handleOpen, handleClose]);

  useEffect(() => {
    if (
      visible &&
      openedAtPathRef.current &&
      location.pathname !== openedAtPathRef.current
    ) {
      setVisible(false);
    }
  }, [location.pathname, visible]);

  return (
    <AntdMobilePopup
      visible={visible}
      onMaskClick={handleClose}
      position="bottom"
      destroyOnClose
      className={`deposit-popup ${className}`}
      maskClassName="deposit-popup__mask"
      bodyClassName="deposit-popup__body"
      bodyStyle={{
        height: "80vh",
        minHeight: "80vh",
        maxHeight: "80vh",
        width: "100%",
        boxSizing: "border-box",
        borderRadius: "1rem 1rem 0 0",
      }}
    >
      <div className="deposit-popup__inner">
        {/* 顶部装饰图：略高于白色面板，形成「嵌入」感 */}

        <div className="deposit-popup__sheet">
          {/* 顶部拖拽条 */}
          <div className="deposit-popup__drag-bar">
            <span className="deposit-popup__drag-indicator" />
          </div>

          <div className="deposit-popup__header">
            <span className="deposit-popup__title">{t("deposit.title")}</span>
            <button
              type="button"
              className="deposit-popup__close"
              onClick={handleClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* 存款保障横幅（设计稿切图） */}
          <div className="deposit-popup__insurance-wrap">
            <img
              src={DepositInsuranceImg}
              alt=""
              className="deposit-popup__insurance-img"
              draggable={false}
            />
          </div>

          {/* 可滚动内容 */}
          <div className="deposit-popup__scroll">
            <Suspense fallback={<ContentSkeleton />}>
              <LazyRecharge
                key={key}
                preferredAmount={openOptions.preferredAmount}
                activityId={openOptions.activityId}
                cashbackBoost={openOptions.cashbackBoost}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </AntdMobilePopup>
  );
};

export default DepositPopup;
